import {
  CalendarOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { DateFormats } from 'infrastructure/enums';
import { DateFormatter } from 'infrastructure/functions';
import BaseDatePicker from 'components/atoms/datePicker';
import classNames from 'classnames';
import ButtonLoadingIcon from 'components/atoms/icons/buttonLoadingIcon';
import { addDays, differenceInCalendarDays } from 'date-fns';
import { useSetDueDate } from 'infrastructure/hooks/ccm/console';
import { useMemo, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import s from './styles.module.scss';

import type { PickerRef } from 'rc-picker';

interface DueDateCellProps {
  date: string | null;
  patientGuid: string;
}

const DueDateCell: FC<DueDateCellProps> = ({ date, patientGuid }) => {
  const [open, setOpen] = useState(false);
  const pickerRef = useRef<PickerRef>(null);
  const { setDueDate, isLoading } = useSetDueDate();
  const { t } = useTranslation();

  const formattedDate = useMemo(() => {
    if (!date) return t('labels.notAvailable');
    return (
      DateFormatter({
        date,
        format: DateFormats['MM/dd/yyyy'],
        toUTC: true,
      }) || t('labels.notAvailable')
    );
  }, [date, t]);

  const dueStatus = useMemo(() => {
    if (!date) return undefined;
    const diff = differenceInCalendarDays(new Date(date), new Date());
    if (diff < 0) {
      return { label: t('labels.overdue'), tone: 'overdue' as const };
    }
    if (diff === 0) {
      return { label: t('labels.dueToday'), tone: 'soon' as const };
    }
    if (diff <= 3) {
      return {
        label: t('labels.dueInDays', { count: diff }),
        tone: 'soon' as const,
      };
    }
    return undefined;
  }, [date, t]);

  const handleClick = () => {
    if (isLoading) return;
    pickerRef.current?.nativeElement.click();
  };

  return (
    <div
      className={classNames(s.wrapper, {
        [s.open]: open,
      })}
      onClick={handleClick}
    >
      <div className={s['date-row']}>
        <p>{formattedDate}</p>
        {isLoading ? (
          <ButtonLoadingIcon className={s['spin-loading-icon']} />
        ) : (
          <CalendarOutlined className={s.icon} />
        )}
      </div>
      {dueStatus && (
        <div className={classNames(s.status, s[dueStatus.tone])}>
          {dueStatus.tone === 'soon' ? (
            <WarningOutlined className={s['status-icon']} />
          ) : (
            <ExclamationCircleOutlined className={s['status-icon']} />
          )}
          <span>{dueStatus.label}</span>
        </div>
      )}
      <BaseDatePicker
        ref={pickerRef}
        value={date ? new Date(date) : null}
        open={open}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
        onOpenChange={(o) => {
          setOpen(o);
        }}
        onChange={(_date, dateString) => {
          if (Array.isArray(dateString)) return;

          setDueDate({
            ccmDueDate: dateString,
            patientGuid,
          });
          handleClick();
        }}
        showNow={false}
        minDate={addDays(new Date(), 1)}
      />
    </div>
  );
};
export default DueDateCell;
